// src/components/AgeProofs/AgeProofs.tsx

import React, { useEffect, useState, useCallback } from "react";
import { AgeProof } from "../../../../../types/ageProofs";
import {
  approveAgeProof,
  approveRevoke,
  getAllUsers,
} from "../../../../../api/adminApi";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TablePagination,
} from "@material-ui/core";

import styles from "./AgeProofsStyles";
import BaseLoading from "../../../../Loader/BaseLoading";
import { showToast } from "../../../../../utils/toast";
import { getFileByIdAPI } from "../../../../../api/userApi";
import videoIconSvg from "../../../../../assets/videoIcon.svg";
import defaultImage from "../../../../../assets/defaultImage.svg";

const AllUsers = () => {
  const [ageProofs, setAgeProofs] = useState<AgeProof[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [, setMediaUrls] = useState<{ [key: string]: string }>({}); // Для BLOB-URL

  const [isLoading, setIsLoading] = useState(true);

  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [sortBy, setSortBy] = useState<string>("createdDate");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  const classes = styles();

  const fetchMediaUrl = async (mediaUrl: string) => {
    if (!mediaUrl) return defaultImage;

    // Якщо це пряме посилання (https:// або http://), використовуємо його напряму
    if (mediaUrl.startsWith("https://") || mediaUrl.startsWith("http://")) {
      setMediaUrls(prev => ({ ...prev, [mediaUrl]: mediaUrl }));
      return mediaUrl;
    }

    const fileId = mediaUrl.split("/api/files/")[1];
    if (!fileId) return defaultImage;

    try {
      const blobUrl = await getFileByIdAPI(fileId);
      setMediaUrls(prev => ({ ...prev, [mediaUrl]: blobUrl }));
      return blobUrl;
    } catch (err) {
      console.error(`Error fetching idProof for ${mediaUrl}:`, err);
      // showToast("Failed to load ID proof image", "error");
      return videoIconSvg;
    }
  };

  const fetchAgeProofs = useCallback(
    (options?: {
      page?: number;
      perPage?: number;
      sortType?: "asc" | "desc";
      sortBy?: string;
    }): Promise<{
      items: AgeProof[];
      totalPages: number;
      currentPage: number;
      totalCount: number;
    }> => {
      return new Promise((resolve, reject) => {
        getAllUsers(options)
          .then(response => resolve(response))
          .catch(error => reject(error));
      });
    },
    []
  );

  // Load data function
  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetchAgeProofs({
        page: page + 1, // API uses 1-based page numbers
        perPage: rowsPerPage,
        sortType: sortOrder,
        sortBy: sortBy,
      });

      setAgeProofs(response?.items || []);
      setTotalCount(response?.totalCount || 0);

      response?.items.forEach(item => {
        if (item.idProof) {
          fetchMediaUrl(item.idProof);
        }
      });
    } catch (error) {
      console.error("Error fetching age proofs:", error);
      showToast("Failed to load age proofs", "error");
    } finally {
      setIsLoading(false);
    }
  }, [page, rowsPerPage, sortOrder, sortBy, fetchAgeProofs]);

  // Load data when page, rowsPerPage, sortOrder, or sortBy changes
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, sortOrder, sortBy]);

  if (isLoading) {
    return <BaseLoading />;
  }

  const handleSort = (field: string) => {
    if (sortBy === field) {
      // Якщо вже сортуємо по цьому полю, змінюємо напрямок
      setSortOrder(prev => (prev === "desc" ? "asc" : "desc"));
    } else {
      // Якщо нове поле, встановлюємо його і починаємо з asc
      setSortBy(field);
      setSortOrder("asc");
    }
    setPage(0); // Скидаємо на першу сторінку при зміні сортування
  };

  const getSortIndicator = (field: string) => {
    if (sortBy !== field) return "";
    return sortOrder === "desc" ? " ↓" : " ↑";
  };

  const handleUserSelection = (userId: string) => {
    setSelectedUsers(prevSelected =>
      prevSelected.includes(userId)
        ? prevSelected.filter(id => id !== userId)
        : [...prevSelected, userId]
    );
  };

  const handleChangeProofStatus = (
    userIds: string[] | string,
    approve: boolean
  ) => {
    const status = approve ? "approved" : "rejected";
    approveAgeProof(userIds, approve)
      .then(() => {
        showToast(`Age proof ${status} successfully!`, "success");
        setAgeProofs(prev =>
          prev.map(proof => {
            if (
              Array.isArray(userIds)
                ? userIds.includes(proof._id)
                : proof._id === userIds
            ) {
              return { ...proof, ageProofed: approve };
            }
            return proof;
          })
        );
      })
      .catch(() => {
        const errStatus = approve ? "approving" : "rejecting";
        console.error(
          `Error ${errStatus} age proof for user with IDs:`,
          userIds
        );
        showToast(`Error ${errStatus} age proof. Please try again.`, "error");
      });
  };
  const handleChangeRevokeStatus = (
    userIds: string[] | string,
    approve: boolean
  ) => {
    const statusText = approve ? "revoked" : "unrevoked";
    approveRevoke(userIds, approve)
      .then(() => {
        showToast(`User(s) successfully ${statusText}`, "success");
        setAgeProofs(prev =>
          prev.map(item => {
            const shouldUpdate = Array.isArray(userIds)
              ? userIds.includes(item._id)
              : item._id === userIds;

            if (shouldUpdate) {
              return { ...item, revoke: approve };
            }
            return item;
          })
        );
      })
      .catch(err => {
        console.error(
          `Error ${approve ? "revoking" : "unrevoking"} users:`,
          userIds,
          err
        );
        showToast(
          `Failed to ${statusText} user(s). Please try again.`,
          "error"
        );
      });
  };

  return (
    <Box sx={{ position: "relative" }} className={classes.pageContainer}>
      <TableContainer component="div" className={classes.tableContainer}>
        <Table style={{ width: "100%" }}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableCell}>Select</TableCell>
              <TableCell
                className={classes.tableCell}
                onClick={() => handleSort("firstName")}
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                Name{getSortIndicator("firstName")}
              </TableCell>
              <TableCell
                className={classes.tableCell}
                onClick={() => handleSort("email")}
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                Email{getSortIndicator("email")}
              </TableCell>
              <TableCell className={classes.tableCell}>User Type</TableCell>
              <TableCell
                className={classes.tableCell}
                onClick={() => handleSort("createdDate")}
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                Date of Submission{getSortIndicator("createdDate")}
              </TableCell>
              <TableCell
                className={classes.tableCell}
                onClick={() => handleSort("revoke")}
                style={{ cursor: "pointer", userSelect: "none" }}
              >
                Revoke Status{getSortIndicator("revoke")}
              </TableCell>
              <TableCell className={classes.tableCell}>Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {ageProofs.map(user => (
              <TableRow
                key={user._id}
                style={{
                  height: "100px",
                  backgroundColor: selectedUsers.includes(user._id)
                    ? !user.ageProofed
                      ? "#76b85a"
                      : "#b85a7c"
                    : "transparent",
                }}
              >
                <TableCell className={classes.tableCell}>
                  <Checkbox
                    style={{ color: "white" }}
                    checked={selectedUsers.includes(user._id)}
                    onChange={() => handleUserSelection(user._id)}
                  />
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {`${user.firstName} ${user.lastName}`}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {user.email}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {user.usertype?.title || "-"}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  {user.createdDate
                    ? new Date(user.createdDate).toLocaleString()
                    : "-"}
                </TableCell>

                <TableCell className={classes.tableCell}>
                  {user.revoke ? "Revoke" : "Not Revoke"}
                </TableCell>
                <TableCell className={classes.tableCell}>
                  <Button
                    onClick={() => handleChangeRevokeStatus(user._id, true)}
                    style={{ marginRight: "10px", color: "green" }}
                    variant="contained"
                  >
                    ✅
                  </Button>
                  <Button
                    onClick={() => handleChangeRevokeStatus(user._id, false)}
                    variant="contained"
                    style={{ color: "red" }}
                  >
                    ❌
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(_e, newPage) => {
          setPage(newPage);
        }}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={e => {
          const newRowsPerPage = parseInt(
            (e.target as HTMLInputElement).value,
            10
          );
          setRowsPerPage(newRowsPerPage);
          setPage(0);
        }}
        rowsPerPageOptions={[10, 20, 25, 50]}
        classes={{
          root: classes.paginationRoot,
          caption: classes.paginationCaption,
          selectIcon: classes.paginationSelectIcon,
        }}
        SelectProps={{ MenuProps: { classes: { paper: classes.menuPaper } } }}
      />

      {selectedUsers.length > 0 && (
        <div
          style={{
            position: "fixed",
            bottom: "20px",
            right: "20px",
            display: "flex",
            gap: "10px",
          }}
        >
          <Button
            onClick={() => handleChangeProofStatus(selectedUsers, true)}
            style={{
              padding: "10px 20px",
              backgroundColor: "#4CAF50",
              color: "white",
              borderRadius: "5px",
            }}
            variant="contained"
          >
            Approve
          </Button>
          <Button
            onClick={() => handleChangeProofStatus(selectedUsers, false)}
            style={{
              padding: "10px 20px",
              backgroundColor: "#f44336",
              color: "white",
              borderRadius: "5px",
            }}
            variant="contained"
          >
            Reject
          </Button>
        </div>
      )}
    </Box>
  );
};

export default AllUsers;
